import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DeviceMobile, Download, AppleLogo, GoogleChromeLogo, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function InstallApp() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    // Already installed check
    if (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone) {
      setInstalled(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      toast.success("App installata!");
      setInstalled(true);
    }
    setPrompt(null);
  };

  const ua = typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : "";
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isDesktop = !isIOS && !isAndroid;

  return (
    <div className="kpi-card max-w-2xl" data-testid="pwa-install-card">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl ht-gradient flex items-center justify-center shrink-0">
          <DeviceMobile size={28} color="white" weight="fill" />
        </div>
        <div className="flex-1">
          <div className="label-eyebrow">App mobile</div>
          <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">
            Installa Housetag sul tuo telefono
          </h3>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Accedi rapidamente al gestionale, ricevi notifiche in tempo reale e usa Housetag come app nativa,
            senza aprire il browser.
          </p>
        </div>
      </div>

      {installed && (
        <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800 flex items-center gap-2">
          ✓ Housetag è installata su questo dispositivo.
        </div>
      )}

      {!installed && prompt && (
        <Button onClick={install} className="ht-gradient border-0 w-full sm:w-auto" data-testid="install-btn">
          <Download size={18} className="mr-2" weight="bold" /> Installa app Housetag
        </Button>
      )}

      {!installed && !prompt && (
        <div className="space-y-3">
          {isIOS && (
            <div className="p-4 rounded-lg bg-purple-50 border border-purple-200 text-sm">
              <div className="flex items-center gap-2 font-semibold mb-2">
                <AppleLogo size={18} weight="fill" /> iPhone / iPad (Safari)
              </div>
              <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
                <li>Tocca il pulsante <strong>Condividi</strong> in basso <span className="inline-block px-1.5 border rounded">⬆</span></li>
                <li>Scorri e tocca <strong>"Aggiungi alla schermata Home"</strong></li>
                <li>Conferma toccando <strong>Aggiungi</strong></li>
              </ol>
            </div>
          )}
          {isAndroid && (
            <div className="p-4 rounded-lg bg-purple-50 border border-purple-200 text-sm">
              <div className="flex items-center gap-2 font-semibold mb-2">
                <GoogleChromeLogo size={18} weight="fill" /> Android (Chrome)
              </div>
              <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
                <li>Tocca il menù <strong>⋮</strong> in alto a destra</li>
                <li>Seleziona <strong>"Installa app"</strong> o <strong>"Aggiungi a schermata Home"</strong></li>
                <li>Conferma toccando <strong>Installa</strong></li>
              </ol>
            </div>
          )}
          {isDesktop && (
            <div className="p-4 rounded-lg bg-purple-50 border border-purple-200 text-sm">
              <div className="flex items-center gap-2 font-semibold mb-2">
                <Info size={18} weight="fill" /> Desktop
              </div>
              <p className="text-muted-foreground">
                Cerca l'icona di installazione <span className="inline-block px-1.5 border rounded">⊕</span> nella barra
                degli indirizzi del browser (Chrome, Edge o Brave), oppure apri questa pagina dal tuo smartphone
                per installare l'app mobile.
              </p>
            </div>
          )}
          {!isIOS && !isAndroid && !isDesktop && (
            <p className="text-sm text-muted-foreground">
              L'installazione non è disponibile su questo dispositivo/browser.
            </p>
          )}
        </div>
      )}

      <div className="mt-6 pt-6 border-t grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
        <div>
          <div className="label-eyebrow">Notifiche</div>
          <p className="text-muted-foreground mt-1">Ricevi avvisi push per messaggi e avanzamenti</p>
        </div>
        <div>
          <div className="label-eyebrow">Offline</div>
          <p className="text-muted-foreground mt-1">Consulta le pratiche anche senza connessione</p>
        </div>
        <div>
          <div className="label-eyebrow">Veloce</div>
          <p className="text-muted-foreground mt-1">Apertura istantanea come app nativa</p>
        </div>
      </div>
    </div>
  );
}
